<template>
  <ChartCard :data-widget-id="widgetDownloadId">
    <ChartHeader :label="label" :widget-download-id="widgetDownloadId" :tooltip-key="tooltipKey" />
    <div class="chart-and-legend-container">
      <div class="chart-container">
        <BarChart :id="widgetDownloadId" :data="chartData" :options="chartOptions" />
      </div>
      <ChartLegend :legend-items="chartLegendItems">
        <template #legend-label="{ item }">
          <div class="completion-chart-legend-item">
            <span class="new-legend-text text-textDarkest">{{ item.value }}</span>
            <span class="new-legend-text">{{ item.label }}</span>
          </div>
        </template>
      </ChartLegend>
    </div>
  </ChartCard>
</template>

<script setup>
import ChartHeader from '@/components/insights/Chart/ChartHeader.vue';
import ChartCard from '@/components/insights/Chart/ChartCard.vue';
import ChartLegend from '@/components/insights/Chart/ChartLegend.vue';
import BarChart from '@/components/insights/Chart/BarChart.vue';
import { computed } from 'vue';
import { useColors } from '@/composables/colors';
import { initTooltip } from '@/components/insights/ProcessInsights/DemographicSurveyReport/charts/helpers/tooltip';
import { numberWithCommas } from '@/helpers/formatters';
import { flatten } from 'lodash-es';

const { candidateNotSubmitted, candidateDeclined, candidateSubmitted } = useColors();

const props = defineProps({
  label: { type: String, required: true },
  data: {
    type: Array,
    required: true,
    validation: (data) => data.length === 3,
  },
  widgetDownloadId: { type: String, default: '' },
  tooltipKey: { type: String, default: '' },
});

const overallTotal = computed(() => props.data[0] + props.data[1] + props.data[2]);

const getDataPercentage = (value) => (value ? (value / overallTotal.value) * 100 : 0);

const chartData = computed(() => ({
  labels: [''],
  datasets: [
    {
      label: 'Submitted',
      data: [getDataPercentage(props.data[0])],
      backgroundColor: candidateSubmitted.value,
      borderRadius: {
        topLeft: 4,
        bottomLeft: 4,
        topRight: 0,
        bottomRight: 0,
      },
      borderSkipped: false,
      count: props.data[0],
    },
    {
      label: 'Declined',
      data: [getDataPercentage(props.data[1])],
      backgroundColor: candidateDeclined.value,
      borderRadius: 0,
      borderSkipped: false,
      count: props.data[1],
    },
    {
      label: 'Not submitted',
      data: [getDataPercentage(props.data[2])],
      backgroundColor: candidateNotSubmitted.value,
      borderRadius: {
        topLeft: 0,
        bottomLeft: 0,
        topRight: 4,
        bottomRight: 4,
      },
      borderSkipped: false,
      count: props.data[2],
    },
  ],
}));

const chartLegendItems = computed(() => chartData.value.datasets.map((dataset) => ({
  label: dataset.label,
  value: typeof dataset?.data?.[0] === 'number' ? `${dataset.data[0].toFixed(1)}%` : '',
  color: dataset.backgroundColor,
})));

const chartOptions = {
  indexAxis: 'y',
  responsive: true,
  maintainAspectRatio: false,
  layout: {
    padding: {
      left: -12,
      right: 5,
    },
  },
  scales: {
    x: {
      min: 0,
      max: 100,
      stacked: true,
      grid: {
        display: false,
      },
      ticks: {
        display: false,
      },
      border: {
        display: false,
      },
    },
    y: {
      stacked: true,
      grid: {
        display: false,
      },
      border: {
        display: false,
      },
      ticks: {
        padding: 10,
      },
    },
  },
  plugins: {
    legend: {
      display: false,
    },
    title: {
      display: false,
    },
    tooltip: {
      enabled: false,
      position: 'nearest',
      external: (context) => initTooltip({
        context,
        tooltipId: 'completion-chart-tooltip',
        createLineItems: (tooltip) => flatten(tooltip.dataPoints.map((point) => [
          { value: `Percentage: ${point.dataset.data[0].toFixed(1)}%` },
          { value: `Count: ${numberWithCommas(point.dataset.count || 0)}` },
        ])),
        createTitle: (tooltip) => tooltip.dataPoints[0].dataset.label,
      }),
    },
  },
};
</script>

<style scoped lang="less">
.chart-and-legend-container {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 165px;
  gap: 16px;
}

.chart-container {
  height: 120px;
  width: 100%;
  margin: 0 auto;
}

.completion-chart-legend-item {
  width: 100%;
  display: grid;
  grid-template-columns: 48px minmax(0, 1fr);
  gap: 4px;
}
</style>
